import type { RestoreAccount } from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { createCombineCondition } from '@/types/advanceFilter';

import { createCloudProviderCondition } from '../cloudProviders/createCloudProviderCondition';
import { convertQuery } from '../cost-explorer/utils/queryUtils';
import { CopyableCode } from '../shared/copyableCode';

export const KmsCopyablePolicy = ({
  cloudProvider,
  withRestoreAccounts = true,
}: {
  cloudProvider: Provider;
  withRestoreAccounts?: boolean;
}) => {
  const dal = useDAL();
  const { data: scanningAccounts, isLoading: isLoadingScanningAccounts } =
    dal.cloudAccounts.scanning.useList();
  const { data: restoreAccounts, isLoading: isLoadingRestoreAccounts } =
    dal.cloudAccounts.restore.useList(undefined, undefined, {
      filters: convertQuery(
        createCombineCondition([createCloudProviderCondition([Provider.Aws])])
      ),
    });

  const filteredScanningAccounts = scanningAccounts?.accounts.filter(
    (a) => a.cloudProvider === cloudProvider
  );

  const isLoading = isLoadingScanningAccounts || isLoadingRestoreAccounts;

  if (!isLoading && filteredScanningAccounts?.length !== 1) {
    console.error(
      "Expected exactly one scanning account for the vault's cloud provider, found:",
      filteredScanningAccounts?.length
    );
    return null;
  }

  const scanningAccountId =
    filteredScanningAccounts?.at(0)?.providerAccountId || '';

  return (
    <CopyableCode
      blur={isLoadingScanningAccounts || isLoadingRestoreAccounts}
      title='policy.json'
      code={getCode(
        scanningAccountId,
        withRestoreAccounts ? (restoreAccounts?.accounts ?? []) : []
      )}
      maxHeight='320px'
    />
  );
};

const getCode = (
  scanningAccountId: string,
  awsRestoreAccounts: RestoreAccount[]
) => {
  const scanningPolicy = `
  {
    "Sid": "Allow key usage in scanning account",
    "Effect": "Allow",
    "Principal": {
      "AWS": "arn:aws:iam::${scanningAccountId}:root"
    },
    "Action": [
      "kms:DescribeKey",
      "kms:Encrypt",
      "kms:Decrypt",
      "kms:ReEncrypt*",
      "kms:GenerateDataKey*"
    ],
    "Resource": "*",
    "Condition": {
      "ArnLike": {
        "aws:PrincipalArn": "arn:aws:iam::${scanningAccountId}:role/Eon*"
      }
    }
  },
  {
    "Sid": "Allow grant for snapshot copy in scanning account",
    "Effect": "Allow",
    "Principal": {
      "AWS": "arn:aws:iam::${scanningAccountId}:root"
    },
    "Action": "kms:CreateGrant",
    "Resource": "*",
    "Condition": {
      "Bool": {
        "kms:GrantIsForAWSResource": "true"
      },
      "ArnLike": {
        "aws:PrincipalArn": "arn:aws:iam::${scanningAccountId}:role/Eon*"
      }
    }
  }`;

  const restorePolicy =
    awsRestoreAccounts.length > 0
      ? `,
  {
    "Sid": "Allow key usage for restore node role and db query",
    "Effect": "Allow",
    "Principal": {
      "AWS": [${awsRestoreAccounts
        .map(
          (a) => `
        "arn:aws:iam::${a.providerAccountId}:root"`
        )
        .join(',')}
      ]
    },
    "Action": [
      "kms:DescribeKey",
      "kms:Encrypt",
      "kms:Decrypt",
      "kms:ReEncrypt*",
      "kms:GenerateDataKey*"
    ],
    "Resource": "*",
    "Condition": {
      "ArnLike": {
        "aws:PrincipalArn": [${awsRestoreAccounts
          .map(
            (a) => `
          "arn:aws:iam::${a.providerAccountId}:role/Eon*"`
          )
          .join(',')}
        ]
      }
    }
  },
  {
    "Sid": "Allow grant for snapshot restore",
    "Effect": "Allow",
    "Principal": {
      "AWS": [${awsRestoreAccounts
        .map(
          (a) => `
        "arn:aws:iam::${a.providerAccountId}:root"`
        )
        .join(',')}
      ]
    },
    "Action": "kms:CreateGrant",
    "Resource": "*",
    "Condition": {
      "Bool": {
        "kms:GrantIsForAWSResource": "true"
      },
      "ArnLike": {
        "aws:PrincipalArn": [${awsRestoreAccounts
          .map(
            (a) => `
          "arn:aws:iam::${a.providerAccountId}:role/Eon*"`
          )
          .join(',')}
        ]
      }
    }
  }`
      : '';

  return `${scanningPolicy}${restorePolicy}`;
};
